/* eslint-disable @nx/enforce-module-boundaries */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { LoginService } from '@ebw-monorepo/login';
import { FeatureSelectService, MapService } from '@ebw-monorepo/map';
import { EbwModalService } from '@ebw-monorepo/modal';
import { SplitViewService } from '@ebw-monorepo/utils';
import { MtxGridColumn, MtxGridRowClassFormatter } from '@ng-matero/extensions/grid';
import { extend } from 'ol/extent';
import { GeoJSON } from 'ol/format';
import { Subject, takeUntil } from 'rxjs';
import { IHttpRequest } from '../../interfaces/httpReq.interface';
import { IGroupButton, IMapTable, IMapTableData } from '../../interfaces/mapTable.interface';
import { API_URLS, ApiUrlService } from '../../../../../utils/src/lib/services/api-url.service';
import { MapRequestService } from '../../services/map-request.service';
import { MapTableService } from '../../services/map-table.service';
import { safeJsonClone } from '@ebw-monorepo/microservices-helpers/utils/parser';

@Component({
  selector: 'ebw-monorepo-otdr-table',
  templateUrl: './otdr-table.component.html',
  styleUrls: ['./otdr-table.component.scss']
})
export class OtdrTableComponent implements OnInit, OnDestroy {
  @ViewChild('geometryColTemplate', { static: true })
  geometryColTemplate!: TemplateRef<any>;

  // --------------------------------------------------------------------------------------------------------------------

  @Output() fullScreenHandler: EventEmitter<boolean> = new EventEmitter();
  @Output() collapseTableHandler: EventEmitter<boolean> = new EventEmitter();

  // --------------------------------------------------------------------------------------------------------------------

  groupButtons: IGroupButton[] = [];
  selectedButton = '';

  public dataGrid = [];
  public columnsGrid: MtxGridColumn[] = [];
  private mapData: IMapTableData[];
  public currentTab: IMapTableData = null;
  public limit = 25;
  private destroy$ = new Subject<void>();
  // --------------------------------------------------------------------------------------------------------------------

  public rowClassFormatter: MtxGridRowClassFormatter = {
    'topologia-LD-sensibile': (data) => {
      if (!data.descrizione) {
        return false;
      }
      return data.descrizione.includes('Topologia LD Sensibili');
    }
  };

  // --------------------------------------------------------------------------------------------------------------------

  private _config: IMapTable = null;

  get config() {
    return this._config;
  }

  @Input() set config(value: IMapTable) {
    this._config = value;
    if (value) {
      this.resetComponentProperties();
      this.getOtdrData();
    }
  }

  // --------------------------------------------------------------------------------------------------------------------

  constructor(
    private mapTableSrv: MapTableService,
    private mapService: MapService,
    private httpClient: HttpClient,
    private ebwModal: EbwModalService,
    private i18NSrv: I18NService,
    private requestSrv: MapRequestService,
    private _featureSelectService: FeatureSelectService,
    private _splitViewService: SplitViewService,
    private _apiUrlService: ApiUrlService
  ) {}

  // --------------------------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this.mapTableSrv
      .getMapTableConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe((conf) => {
        if (conf) {
          this.config = conf;
        }
      });
  }

  // --------------------------------------------------------------------------------------------------------------------

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.closeMapTable();
  }

  // --------------------------------------------------------------------------------------------------------------------

  closeMapTable() {
    this.mapTableSrv.setMapTableConfig(null);
    this.mapService.clearAllFeaturesFromTemporaryLayer();
    this.mapService.clearAllFeaturesFromTemporaryLayer(true);
  }

  // --------------------------------------------------------------------------------------------------------------------

  /**
   * function to execute a query and obtain its data
   */
  private getOtdrData() {
    // setup the required headers
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY)
    });

    // make the http request to execute the query and retrieve the records
    this.httpClient.get(this.config.url, { headers }).subscribe({
      next: (resp: IMapTableData[]) => {
        // store the data received
        const tabs = resp;
        // creo gli oggetti tabs
        this.groupButtons = this.mapTableSrv.createMapButtonsGroup(tabs);
        // seleziono la prima
        this.selectedButton = this.groupButtons[0].label;
        // Qui recupero le informazioni descrittive della procedurq di Otdr
        const additionalInfoTab = tabs.find((tab) => tab.tab_name === 'Additional_info');
        if (additionalInfoTab) {
          const additionalInfo = additionalInfoTab.data[0] as any;
          let subtitle = additionalInfo.section_description;
          if (additionalInfo.obj_desc) {
            subtitle += ': ' + additionalInfo.obj_desc;
          }
          if (additionalInfo.obj_id) {
            subtitle += ', id: ' + additionalInfo.obj_id;
          }
          this.updateSplitViewConf({ subtitle });
          this.mapData = [...tabs.filter((tab) => tab.tab_name !== 'Additional_info')];
        }

        // Aggiungo in automatico layers su mappa se ho showGeometries = true
        let defaultLayers = this.mapData.filter((m) => m.showGeometries);
        const format = new GeoJSON();
        if (defaultLayers && defaultLayers.length > 0) {
          this.mapService.clearAllFeaturesFromTemporaryLayer();
          let layerExtent = null;
          defaultLayers.forEach((d, i) => {
            d.data.map((el, y) => {
              // Aggiungo layer temporaneo per ogni riga della tabella
              this.mapService.addFeatureToTemporaryLayer(el.geometry, null, false, false, null, `${y + 1}`);

              const options = {
                dataProjection: MapService.EPSG_3857_KEY,
                featureProjection: MapService.EPSG_3857_KEY
              };
              const geomExtent = format.readGeometry(el.geometry, options).getExtent();

              if (!layerExtent) {
                layerExtent = geomExtent;
              }
              layerExtent = extend(layerExtent, geomExtent);
            });
          });

          // prepare a default size in case the map dimensions are 0
          const size = this.mapService.openLayersMap.getSize();

          if (size[0] === 0) {
            size[0] = 500;
          }
          if (size[1] === 0) {
            size[1] = 500;
          }

          this.mapService.openLayersMap.getView().fit(layerExtent, { size: size });
        }
        // initialize the table with the new data
        this.initializeTable();
      },
      error: (err) => {
        this.ebwModal.openErrorModal({
          title: this.i18NSrv.getInstantValue('common.error'),
          content: err.message
        });
      }
    });
  }

  // ------------------------------------------------------------------------------------------------------------

  /**
   *
   * @param element singolo elemento della tabella
   * @param forced parametro che indica se interrompere forzatamente la procedura dopo 1 ciclo
   * @returns void
   */
  async centerMapPlace(element: any, forced = false) {
    // Se esiste la geometria centro la mappa subito sulla geometria
    if (element.geometry) {
      this.mapService.clearAllFeaturesFromTemporaryLayer();
      this.updateSplitViewConf(null, { halfScreen: true, collapsed: false });

      this.mapService.addFeatureToTemporaryLayer(element.geometry, null, false, false);
      setTimeout(() => {
        this.mapService.centerMapOnGeometry(element.geometry);
      }, 0);
      return;
    }
    // se true esco
    if (forced) {
      return;
    }
    // se non ho la geometria nell'oggetto cerco di ricavarmela attraverso il suo URN (se presente)
    const elementWithGeometry = await this.getElementGeometry(element);
    if (elementWithGeometry) {
      this.centerMapPlace(elementWithGeometry, true);
    }
  }

  // ------------------------------------------------------------------------------------------------------------

  async getElementGeometry(element) {
    try {
      // Cerco ricavarmi l'URN cercando se ci sono proprietá con una chiave simile
      const key = Object.keys(element).find((key) => key === 'urn');
      const urn = element[key];
      // se urn non esiste esco
      if (!urn) {
        throw Error();
      }
      // recupero la geometria tramite urn via API
      const response = await this._featureSelectService.selectByUrns([urn]);
      // se errore esco
      if (response.error) {
        throw Error();
      }
      // Estraggo feature in questione
      const feature = response.result.features[0];
      if (!feature.geometry) {
        // cerco di ereditare le geometrie da asset contenitori
        const inheritedGeometry = await this._featureSelectService.getPlacedAssetGeometry(feature);
        // se non riesco ad ottenerla ritorno oggetto al componente perchè si aggiorni internamente
        if (inheritedGeometry) {
          feature.geometry = inheritedGeometry;
        }
      }
      if (feature.geometry) {
        const format = new GeoJSON();
        const readOptions = {
          dataProjection: MapService.EPSG_4326_KEY,
          featureProjection: MapService.EPSG_3857_KEY
        };
        // Leggo geometria nel sistema di riferimento corretto
        const geomR = format.readGeometry(feature.geometry, readOptions);
        const writeOptions = {
          dataProjection: MapService.EPSG_3857_KEY,
          featureProjection: MapService.EPSG_3857_KEY
        };
        // la creo nel formato che vuole OpenLayer
        const geomW = format.writeGeometry(geomR, writeOptions);
        // Ricostruisco oggetto che vuole il metodo centerMapPlace
        return { geometry: JSON.parse(geomW) };
      }
      return false;
    } catch (error) {
      console.error('[OtdrTableComponent][getElementGeometry]', error);
      this.ebwModal.openErrorModal({
        title: 'Dato non disponibile',
        content: 'Non é stato possibile recuperare la geometria di questo oggetto'
      });
      return false;
    }
  }

  // ------------------------------------------------------------------------------------------------------------

  getInitTableFromName(event: any) {
    // Aggiorno il valore selezionato
    this.selectedButton = event.value;
    // aggiorno la lista
    this.initializeTable(true);
    // rimuovo i marker dalla mappa
    this.mapService.removeAllMarkerFromTemporaryLayer();
  }

  // ------------------------------------------------------------------------------------------------------------

  /**
   * function to initialize the table
   * @param fromTabChange
   * @returns
   */
  initializeTable(fromTabChange = false) {
    this.currentTab = this.mapData.find((t) => t.tab_name === this.selectedButton);
    // se non trovo la tab corrente esco
    if (!this.currentTab) {
      console.warn('[OtdrTable][initializeTable] Tab corrente non trovata');
      return;
    }

    let tempColumnsGrid: MtxGridColumn[] = [];

    if (this.currentTab.data.length > 0) {
      const props = this.currentTab.data[0].properties;
      for (const [key, value] of Object.entries(props)) {
        const column: MtxGridColumn = {
          header: key,
          field: key,
          minWidth: 180
        };

        // Aggiungo tutte le colonne tranne l'urn
        if (key !== 'urn') {
          tempColumnsGrid.push(column);
        }
      }

      // Aggiungo una colonna per poter posizionare oggetto su mappa (template custom)
      tempColumnsGrid.unshift({
        header: 'Geometria',
        field: 'geometry',
        width: '50px',
        pinned: 'left',
        cellTemplate: this.geometryColTemplate
      });

      this.columnsGrid = tempColumnsGrid;
      this.dataGrid = this.currentTab.data.map((feature) => ({
        geometry: feature.geometry,
        ...feature.properties
      }));
      return;
    }

    if (fromTabChange) {
      this.dataGrid = [];
      this.columnsGrid = [];
    }
  }

  // ------------------------------------------------------------------------------------------------------------

  export(type: 'excel' | 'csv' | 'pdf') {
    let exportApiUrl = '';
    let body = null;
    let blobType = 'text/plain';

    let activeTab: IMapTableData = safeJsonClone(this.currentTab);
    activeTab.tab_name = activeTab.tab_name.replace(' ', '_');
    // se non ho elementi esco
    if (!activeTab?.data?.length) {
      this.ebwModal.openInfoModal({
        title: this.i18NSrv.getInstantValue('map.map_table.no_data_to_export'),
        content: ''
      });
      return;
    }
    // altrimenti per ogni elemento
    activeTab.data = activeTab.data.map((d) => {
      // elimino la proprietá 'urn' per non farla apparire nei file esportati
      delete d.properties.urn;
      return d;
    });

    if (type === 'excel') {
      exportApiUrl = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.EXCEL);
      body = { data: this.mapTableSrv.formatExportData(activeTab, false, true) };
      blobType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }
    if (type === 'csv') {
      exportApiUrl = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.CSV);
      body = { data: this.mapTableSrv.formatExportData(activeTab) };
      blobType = 'text/csv';
    }
    if (type === 'pdf') {
      exportApiUrl = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.PDF);
      body = { data: this.mapTableSrv.formatExportData(activeTab, true) };
      blobType = 'application/pdf';
    }

    const assetId = activeTab?.tab_description?.split('id: ')?.reverse()[0] || '';
    const filename = `OTDR_sezione_${activeTab.tab_name}_id_${assetId}`;
    const url = `${exportApiUrl}?filename=${filename}`;
    const req: IHttpRequest = { url };
    const opt = { responseType: 'blob' };
    this.requestSrv.postMapRequest(req, body, opt).subscribe({
      next: (data: any) => {
        let downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(new Blob([data], { type: blobType }));
        downloadLink.setAttribute('download', filename);
        document.body.appendChild(downloadLink);
        downloadLink.click();
      }
    });
  }

  updateSplitViewConf(conf, layoutConf = null) {
    let currentConf = this._splitViewService.viewConfig$.value;
    let currentLayoutConf = this._splitViewService.viewLayountConfig$.value;

    if (currentConf && currentLayoutConf && currentLayoutConf.isEnable) {
      if (conf) {
        currentConf = Object.assign({}, currentConf, conf);
        this._splitViewService.confChange(currentConf);
      }
      if (layoutConf) {
        currentLayoutConf = Object.assign({}, currentLayoutConf, layoutConf);
        this._splitViewService.layoutConfChange(currentLayoutConf);
      }
    }
  }
  // ------------------------------------------------------------------------------------------------------------

  resetComponentProperties() {
    this.groupButtons = [];
    this.selectedButton = '';
    this.currentTab = null;
    this.columnsGrid = [];
    this.dataGrid = [];
    this.mapService.clearAllFeaturesFromTemporaryLayer();
  }
}
